import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { SITE_CONFIG } from '../utils/constants';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        program: '',
        message: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const text = `Hi, I'm ${formData.name}. Interested in ${formData.program}. ${formData.message}. Phone: ${formData.phone}`;
        const url = `https://wa.me/${SITE_CONFIG.whatsappNumber}?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank');
    };

    return (
        <div className="page-wrapper">
            <div className="page-header">
                <div className="container text-center">
                    <h1 className="page-title">Contact Us</h1>
                    <p className="page-subtitle">Get in touch to know more about our admission process.</p>
                </div>
            </div>

            <div className="container section">
                <div className="contact-grid">
                    <div className="contact-info">
                        <h2 className="section-title">Get In Touch</h2>
                        <p className="contact-desc">
                            Have questions? We'd love to hear from you. Send us a message
                            and we'll respond as soon as possible.
                        </p>

                        <div className="info-item">
                            <div className="info-icon">
                                <Phone size={20} />
                            </div>
                            <div>
                                <h4 className="info-lbl">Phone</h4>
                                <p>{SITE_CONFIG.phone}</p>
                            </div>
                        </div>

                        <div className="info-item">
                            <div className="info-icon">
                                <Mail size={20} />
                            </div>
                            <div>
                                <h4 className="info-lbl">Email</h4>
                                <p>{SITE_CONFIG.email}</p>
                            </div>
                        </div>

                        <div className="info-item">
                            <div className="info-icon">
                                <MapPin size={20} />
                            </div>
                            <div>
                                <h4 className="info-lbl">Campus Location</h4>
                                <p>{SITE_CONFIG.address}</p>
                            </div>
                        </div>

                        <div className="map-wrapper">
                            <iframe
                                src={SITE_CONFIG.mapLink}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="School Location"
                            ></iframe>
                        </div>
                    </div>

                    <div className="contact-form-wrapper">
                        <form className="contact-form" onSubmit={handleSubmit}>
                            <h3 className="form-title">Admission Enquiry</h3>

                            <div className="form-group">
                                <label>Parent's Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    className="form-input"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter your name"
                                />
                            </div>

                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    className="form-input"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Enter your email"
                                />
                            </div>

                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    required
                                    className="form-input"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="Enter your phone number"
                                />
                            </div>

                            <div className="form-group">
                                <label>Interested Program</label>
                                <select
                                    name="program"
                                    className="form-input"
                                    value={formData.program}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Program</option>
                                    <option value="Play Group">Play Group</option>
                                    <option value="Nursery">Nursery</option>
                                    <option value="Junior KG">Junior KG</option>
                                    <option value="Senior KG">Senior KG</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Message</label>
                                <textarea
                                    name="message"
                                    className="form-input textarea"
                                    rows="4"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Any specific questions?"
                                ></textarea>
                            </div>

                            <button type="submit" className="btn btn-primary w-full">
                                <Send size={18} className="mr-2" /> Send via WhatsApp
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            <style>{`
            .page-header { background-color: var(--color-success); }
            .mr-2 { margin-right: 0.5rem; }
            .w-full { width: 100%; }

            .contact-grid {
                display: grid;
                grid-template-columns: 1fr 1.2fr;
                gap: 4rem;
                align-items: start;
            }

            .contact-desc {
                font-size: 1.1rem;
                color: var(--color-text-light);
                margin-bottom: 2rem;
            }

            .info-item {
                display: flex;
                gap: 1.5rem;
                margin-bottom: 2rem;
            }

            .info-icon {
                width: 50px;
                height: 50px;
                background-color: var(--color-bg-light);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: var(--color-primary);
                flex-shrink: 0;
            }

            .info-lbl {
                font-weight: 700;
                color: var(--color-text-main);
                margin-bottom: 0.25rem;
            }

            .contact-form-wrapper {
                background: white;
                padding: 3rem;
                border-radius: 2rem;
                box-shadow: var(--shadow-xl);
                border: 1px solid #f1f5f9;
            }

            .form-title {
                font-size: 1.75rem;
                font-weight: 700;
                margin-bottom: 2rem;
                text-align: center;
            }

            .form-group {
                margin-bottom: 1.5rem;
            }

            .form-group label {
                display: block;
                font-weight: 600;
                margin-bottom: 0.5rem;
                font-size: 0.9rem;
                color: var(--color-text-main);
            }

            .form-input {
                width: 100%;
                padding: 0.875rem;
                border-radius: 0.75rem;
                border: 2px solid #e2e8f0;
                font-family: var(--font-body);
                transition: border-color 0.2s;
            }

            .form-input:focus {
                outline: none;
                border-color: var(--color-primary);
            }
            
            .textarea { resize: vertical; }

            .map-wrapper {
                height: 300px;
                background-color: #e2e8f0;
                border-radius: 1.5rem;
                margin-top: 2rem;
                overflow: hidden;
                box-shadow: var(--shadow-md);
            }

            @media (max-width: 960px) {
                .contact-grid {
                    grid-template-columns: 1fr;
                    gap: 3rem;
                }
                
                .contact-form-wrapper {
                    padding: 2rem;
                }
            }
`}</style>
        </div>
    );
};

export default Contact;
